import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Plus, Trash2, Edit3 } from "lucide-react";
import RulesService, { type Rule } from "../../../_services/RulesService";
import RuleEditorModal from "./RuleEditorModal";
import RuleDetail from "./RuleDetail";
import { predicateToString } from "./RuleSummary";

interface BadgeOption {
  Id: number;
  Name: string;
  AchievementType?: string;
}

interface Props {
  badges: BadgeOption[];
}

/** Top-level "Issuance Rules" tab content. List + editor + detail. */
const RulesTab: React.FC<Props> = ({ badges }) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Rule | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRuleId, setDetailRuleId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res: any = await RulesService.list({ limit: 100 });
      setRules((res?.data?.data || []) as Rule[]);
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.message || "Failed to load rules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (rule: Rule) => { setEditing(rule); setEditorOpen(true); };
  const openDetail = (rule: Rule) => { setDetailRuleId(rule.id); setDetailOpen(true); };

  const remove = async (rule: Rule) => {
    if (!window.confirm(`Disable rule "${rule.name}"? (Soft-delete — sets EnableStatus=0)`)) return;
    try {
      await RulesService.remove(rule.id);
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.message || "Delete failed");
    }
  };

  return (
    <div className="ca-rules-tab">
      <div className="ca-rules-tab-head">
        <div>
          <h2>Issuance Rules</h2>
          <p>Rule-based badge issuance — predicate evaluations triggered by student events.</p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={14} /> New Rule
        </Button>
      </div>

      {err && <div className="bm-error">{err}</div>}

      {loading ? (
        <div className="ca-rules-empty">Loading rules…</div>
      ) : rules.length === 0 ? (
        <div className="ca-rules-empty">
          No rules yet. Click <strong>+ New Rule</strong> to create the first one.
        </div>
      ) : (
        <div className="ca-rules-list">
          {rules.map((r) => (
            <div
              key={r.id}
              className={`ca-rules-row ${r.enableStatus ? "" : "is-disabled"}`}
              onClick={() => openDetail(r)}
            >
              <div className="ca-rules-row-main">
                <div className="ca-rules-row-name">
                  {r.name}
                  {!r.enableStatus && <span className="ca-rules-row-disabled">disabled</span>}
                </div>
                <div className="ca-rules-row-badge">
                  <strong>{r.badgeName}</strong>
                  {r.badgeAchievementType && (
                    <span className="ca-rules-row-pill">{r.badgeAchievementType}</span>
                  )}
                </div>
                <code className="ca-rules-row-pred">{predicateToString(r.predicate)}</code>
                <div className="ca-rules-row-triggers">
                  {(r.triggers?.events || []).map((e) => (
                    <span key={e} className="ca-rules-trigger-chip">{e}</span>
                  ))}
                </div>
              </div>
              <div className="ca-rules-row-meta">
                <div className="ca-rules-row-mode">
                  {r.reviewRequired ? "Review-required" : "Auto-issue"}
                </div>
                <div className="ca-rules-row-stats">
                  <span title="Fires in last 7 days">
                    <strong>{r.fires7d ?? 0}</strong> fires (7d)
                  </span>
                  {(r.pendingEligibility ?? 0) > 0 && (
                    <span className="ca-rules-row-pending" title="Pending review">
                      {r.pendingEligibility} pending
                    </span>
                  )}
                </div>
              </div>
              <div className="ca-rules-row-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="ca-rules-iconbtn"
                  title="Edit rule"
                  onClick={() => openEdit(r)}
                >
                  <Edit3 size={14} />
                </button>
                <button
                  type="button"
                  className="ca-rules-iconbtn danger"
                  title="Disable rule"
                  onClick={() => remove(r)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleEditorModal
        show={editorOpen}
        onHide={() => setEditorOpen(false)}
        rule={editing}
        badges={badges}
        onSaved={() => { setEditorOpen(false); load(); }}
      />

      <RuleDetail
        show={detailOpen}
        onHide={() => setDetailOpen(false)}
        ruleId={detailRuleId}
        onEdit={(r) => { setDetailOpen(false); setEditing(r); setEditorOpen(true); }}
      />
    </div>
  );
};

export default RulesTab;
